// ParentVisitsCalendar.js
import React, { useEffect, useState, useMemo } from 'react';
import API from './api';
import { FaBookOpen } from 'react-icons/fa';

/** ---------- Helpers ---------- */
function pad2(n) {
  return String(n).padStart(2, '0');
}

function addMinutesHHMM(hhmm, mins) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date(2000, 0, 1, h || 0, m || 0, 0, 0);
  d.setMinutes(d.getMinutes() + (mins || 0));
  const hh = pad2(d.getHours());
  const mm = pad2(d.getMinutes());
  return `${hh}:${mm}`;
}

function groupByDate(visits) {
  return visits.reduce((acc, v) => {
    const date = v.scheduled_at.slice(0, 10);
    if (!acc[date]) acc[date] = [];
    acc[date].push(v);
    return acc;
  }, {});
}

const isEventVisit = (v) =>
  v.type === 'event' ||
  v.visit_type === 'event' ||
  v.is_event === 1 ||
  v.is_event === true;

/** ---------- Badges ---------- */

function StatusBadge({ status, children }) {
  let color = 'bg-gray-200 text-gray-700';
  if (status === 'pending') color = 'bg-yellow-100 text-yellow-800';
  if (status === 'accepted') color = 'bg-green-100 text-green-700';
  if (status === 'rejected') color = 'bg-red-100 text-red-700';
  if (status === 'draft') color = 'bg-blue-100 text-blue-700';
  if (status === 'complete') color = 'bg-green-200 text-green-900';
  if (status === 'canceled') color = 'bg-purple-200 text-purple-700';
  return (
    <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${color}`}>
      {children || status}
    </span>
  );
}

// discipline badge (EI/PI/ABA/OT/PT…)
function ProgramBadge({ program_type, discipline_code }) {
  const raw = (discipline_code || program_type || '').toString().trim();
  if (!raw) return null;

  const code = raw.replace(/^PROG_/i, '').toUpperCase();

  let extra = '';
  let label = code;

  if (code === 'PI') {
    extra = ' bg-orange-100 text-orange-800 border border-orange-200';
    label = 'PI';
  } else if (code === 'EI') {
    extra = ' bg-blue-100 text-blue-800 border border-blue-200';
    label = 'EI';
  } else if (code === 'ABA') {
    extra = ' bg-purple-100 text-purple-800 border border-purple-200';
    label = 'ABA';
  } else if (code === 'OT') {
    extra = ' bg-teal-100 text-teal-800 border border-teal-200';
    label = 'OT';
  } else if (code === 'PT') {
    extra = ' bg-emerald-100 text-emerald-800 border border-emerald-200';
    label = 'PT';
  } else if (code === 'ST') {
    extra = ' bg-green-100 text-green-800 border border-green-200';
    label = 'ST';
  } else {
    extra = ' border border-gray-400 text-gray-700 bg-gray-50';
    label = code;
  }

  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${extra}`}>
      {label}
    </span>
  );
}

function EventBadge({ isEvent }) {
  if (!isEvent) return null;
  return (
    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300">
      EVENT
    </span>
  );
}

// mali kolor-coded dotovi po disciplini
function MiniDisciplineDots({ dayVisits }) {
  if (!dayVisits || dayVisits.length === 0) return null;

  const codes = new Set();
  let hasEvent = false;

  dayVisits.forEach((v) => {
    if (isEventVisit(v)) hasEvent = true;
    const raw = (v.discipline_code || v.program_type || '').toString().trim();
    if (!raw) return;
    const c = raw.replace(/^PROG_/i, '').toUpperCase();
    codes.add(c);
  });

  const dots = [];
  [...codes].slice(0, 4).forEach((code) => {
    let cls = 'bg-gray-400';
    if (code === 'EI') cls = 'bg-blue-400';
    else if (code === 'PI') cls = 'bg-orange-400';
    else if (code === 'ABA') cls = 'bg-purple-400';
    else if (code === 'OT') cls = 'bg-teal-400';
    else if (code === 'PT') cls = 'bg-emerald-400';
	else if (code === 'ST') cls = 'bg-green-400';

    dots.push(
      <span
        key={code}
        className={`w-2 h-2 rounded-full ${cls}`}
        title={code}
      />
    );
  });

  if (hasEvent) {
    dots.push(
      <span
        key="EVENT"
        className="w-2 h-2 rounded-full bg-yellow-200 border border-yellow-500"
        title="EVENT"
      />
    );
  }

  return <div className="flex flex-wrap gap-1 mt-1">{dots}</div>;
}

// samo vreme
function VisitTime({ scheduled_at, event_end_time }) {
  const from = scheduled_at?.slice(11, 16) || '';
  let to = null;

  if (event_end_time) {
    to = event_end_time.slice(0, 5);
  } else if (from) {
    to = addMinutesHHMM(from, 45);
  }

  return (
    <span className="font-mono text-sm">
      {from && to ? `${from}–${to}` : from || '-'}
    </span>
  );
}

/** ---------- Main component ---------- */

function ParentVisitsCalendar() {
  const [monthCursor, setMonthCursor] = useState(null); // Date pointing to 1st of month
  const [todayStr, setTodayStr] = useState(null); // "YYYY-MM-DD"
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedDate, setSelectedDate] = useState(null);

  // Notes history modal
  const [showNotesHistory, setShowNotesHistory] = useState(false);
  const [notesHistory, setNotesHistory] = useState([]);
  const [notesHistoryLoading, setNotesHistoryLoading] = useState(false);
  const [notesHistoryStudent, setNotesHistoryStudent] = useState(null);

  // 1) server-time -> todayStr + initial monthCursor
  useEffect(() => {
    let isMounted = true;
    API.get('/server-time')
      .then((res) => {
        if (!isMounted) return;
        const dateStr = res.data.serverTime.slice(0, 10); // "YYYY-MM-DD"
        setTodayStr(dateStr);

        const [y, m] = dateStr.split('-').map(Number);
        const firstOfMonth = new Date(y, m - 1, 1);
        setMonthCursor(firstOfMonth);
      })
      .catch(() => {
        // fallback na lokalni datum ako server-time pukne
        const d = new Date();
        const dateStr = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(
          d.getDate()
        )}`;
        setTodayStr(dateStr);
        const firstOfMonth = new Date(d.getFullYear(), d.getMonth(), 1);
        setMonthCursor(firstOfMonth);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // 2) kad se promeni mesec -> povuci posete za ceo mesec
  useEffect(() => {
    if (!monthCursor) return;

    const y = monthCursor.getFullYear();
    const m = monthCursor.getMonth() + 1;

    const from = `${y}-${pad2(m)}-01`;
    const lastDay = new Date(y, m, 0).getDate();
    const to = `${y}-${pad2(m)}-${pad2(lastDay)}`;

    async function load() {
      setLoading(true);
      try {
        const res = await API.get('/parent/visits', {
          params: { date_from: from, date_to: to },
        });
        const rows = res.data || [];
        setVisits(rows);

        // ako još nemamo selectedDate, pokušaj:
        setSelectedDate((prev) => {
          if (prev) return prev;

          // ako je today u ovom mesecu -> selektuj today
          if (todayStr && todayStr.slice(0, 7) === from.slice(0, 7)) {
            return todayStr;
          }

          // inače prvi dan koji ima posete
          const groupedLocal = groupByDate(rows);
          const dates = Object.keys(groupedLocal).sort();
          return dates.length ? dates[0] : null;
        });
      } catch (err) {
        console.error('Failed to load parent visits for month:', err);
        setVisits([]);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [monthCursor, todayStr]);

  const grouped = useMemo(() => groupByDate(visits), [visits]);

  /** ---------- Calendar grid ---------- */

  const handlePrevMonth = () => {
    if (!monthCursor) return;
    const d = new Date(monthCursor);
    d.setMonth(d.getMonth() - 1);
    setMonthCursor(d);
  };

  const handleNextMonth = () => {
    if (!monthCursor) return;
    const d = new Date(monthCursor);
    d.setMonth(d.getMonth() + 1);
    setMonthCursor(d);
  };

  const goToToday = () => {
    if (!todayStr) return;
    const [y, m] = todayStr.split('-').map(Number);
    const firstOfMonth = new Date(y, m - 1, 1);
    setMonthCursor(firstOfMonth);
    setSelectedDate(todayStr);
  };

  function buildCalendarCells() {
    if (!monthCursor) return [];

    const y = monthCursor.getFullYear();
    const m = monthCursor.getMonth(); // 0-based

    const firstOfMonth = new Date(y, m, 1);
    const firstWeekday = firstOfMonth.getDay(); // 0=Sun..6=Sat (US style)
    const daysInMonth = new Date(y, m + 1, 0).getDate();

    const cells = [];

    // prazne ćelije pre 1. u mesecu
    for (let i = 0; i < firstWeekday; i++) {
      cells.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const d = new Date(y, m, day);
      const dateStr = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
      cells.push({ dateObj: d, dateStr });
    }

    return cells;
  }

  const calendarCells = buildCalendarCells();

  const monthLabel = monthCursor
    ? monthCursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '';

  /** ---------- Notes history ---------- */

  const openNotesHistoryModal = async (studentId, studentName) => {
    setNotesHistoryLoading(true);
    setShowNotesHistory(true);
    setNotesHistoryStudent(studentName);
    try {
      const res = await API.get(`/students/${studentId}/notes-history`);
      setNotesHistory(res.data || []);
    } catch {
      setNotesHistory([]);
    } finally {
      setNotesHistoryLoading(false);
    }
  };

  /** ---------- Selected day visits ---------- */

  const selectedVisits = selectedDate ? grouped[selectedDate] || [] : [];

  return (
    <div className="w-full px-2 sm:px-4">
      <h2 className="text-lg font-bold mb-4">Visits Calendar</h2>

      {/* Header: Month nav + Today button */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handlePrevMonth}
            className="px-2 py-1 text-sm border rounded hover:bg-gray-100"
          >
            ‹
          </button>
          <span className="font-semibold">{monthLabel}</span>
          <button
            type="button"
            onClick={handleNextMonth}
            className="px-2 py-1 text-sm border rounded hover:bg-gray-100"
          >
            ›
          </button>
        </div>
        <button
          type="button"
          onClick={goToToday}
          disabled={!todayStr}
          className={`px-3 py-1 text-xs sm:text-sm rounded border ${
            todayStr
              ? 'border-blue-500 text-blue-700 hover:bg-blue-50'
              : 'border-gray-300 text-gray-400 cursor-not-allowed'
          }`}
        >
          Today
        </button>
      </div>

      {/* Calendar + side panel */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Calendar grid */}
        <div className="lg:w-2/3 bg-white border rounded-lg shadow-sm">
          <div className="grid grid-cols-7 text-xs font-semibold text-center border-b bg-gray-50">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d} className="py-2">
                {d}
              </div>
            ))}
          </div>

          {loading && (
            <div className="p-3 text-center text-gray-500 text-sm">
              Loading visits...
            </div>
          )}

          <div className="grid grid-cols-7 gap-px bg-gray-200 text-xs">
            {calendarCells.map((cell, idx) => {
              if (cell === null) {
                return (
                  <div
                    key={`empty-${idx}`}
                    className="min-h-[72px] bg-gray-50"
                  />
                );
              }

              const { dateStr } = cell;
              const dayVisits = grouped[dateStr] || [];
              const isSelected = selectedDate === dateStr;
              const isToday = todayStr === dateStr;

              // STATUS RING – tačka 1
              const hasCanceled = dayVisits.some((v) => v.status === 'canceled');
              const hasComplete = dayVisits.some((v) => v.status === 'complete');
              const hasPending = dayVisits.some((v) => v.status === 'pending');
              const hasDraft = dayVisits.some((v) => v.status === 'draft');

              let ringClass = '';
              if (hasCanceled) ringClass = 'ring-2 ring-purple-400';
              else if (hasComplete) ringClass = 'ring-2 ring-green-400';
              else if (hasPending) ringClass = 'ring-2 ring-yellow-400';
              else if (hasDraft) ringClass = 'ring ring-blue-300';

              const baseBg = dayVisits.length
                ? 'bg-white hover:bg-blue-50 cursor-pointer'
                : 'bg-gray-50 hover:bg-gray-100 cursor-pointer';

              const selectedBg = isSelected ? 'bg-blue-50 border-blue-400' : '';
              const todayText = isToday ? 'text-blue-700 font-bold' : '';

              return (
                <div
                  key={dateStr}
                  onClick={() => setSelectedDate(dateStr)}
                  className={`relative min-h-[80px] p-1 border border-gray-200 ${baseBg} ${selectedBg} ${ringClass}`}
                >
                  <div className={`flex justify-between items-center`}>
                    <span className={`text-xs ${todayText}`}>
                      {parseInt(dateStr.slice(-2), 10)}
                    </span>
                    {isToday && (
                      <span className="ml-1 text-[10px] px-1 py-0.5 rounded-full bg-green-100 text-green-700">
                        Today
                      </span>
                    )}
                  </div>

                  {/* mini dotovi za discipline / event */}
                  <MiniDisciplineDots dayVisits={dayVisits} />

                  {dayVisits.length > 0 && (
                    <div className="mt-1 text-[10px] text-gray-500">
                      {dayVisits.length} visit{dayVisits.length > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Side panel – detalji za selectedDate + tačka 5 (Today badge / Jump to today) */}
        <div className="lg:w-1/3 bg-white border rounded-lg shadow-sm p-3 flex flex-col">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="text-sm font-semibold">
                {selectedDate ? `Visits on ${selectedDate}` : 'Select a day'}
              </div>
              {selectedDate && todayStr && selectedDate === todayStr ? (
                <span className="inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-800">
                  Today
                </span>
              ) : (
                todayStr && (
                  <button
                    type="button"
                    onClick={goToToday}
                    className="inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full border border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    Jump to today
                  </button>
                )
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {!selectedDate ? (
              <div className="text-xs text-gray-500 mt-2">
                Click on a day in the calendar to see details.
              </div>
            ) : selectedVisits.length === 0 ? (
              <div className="text-xs text-gray-500 mt-2">
                No visits scheduled for this day.
              </div>
            ) : (
              <div className="space-y-2">
                {selectedVisits.map((v) => {
                  const eventFlag = isEventVisit(v);
                  return (
                    <div
                      key={v.visit_id}
                      className="border rounded-lg p-2 shadow-sm bg-gray-50"
                    >
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm">
                              {v.student_name}
                            </span>
                            {eventFlag ? (
                              <EventBadge isEvent={true} />
                            ) : (
                              <ProgramBadge
                                program_type={v.program_type}
                                discipline_code={v.discipline_code}
                              />
                            )}
                            <button
                              onClick={() =>
                                openNotesHistoryModal(
                                  v.student_id,
                                  v.student_name
                                )
                              }
                              className="ml-1 text-purple-700 hover:text-purple-900"
                              title="Show Notes History"
                            >
                              <FaBookOpen size={14} />
                            </button>
                          </div>
                          <div className="mt-1 text-xs text-gray-600">
                            Provider: {v.teacher_name}
                            {v.teacher_id !== v.original_teacher_id &&
                              v.original_teacher_id && (
                                <span className="ml-1 text-gray-400">
                                  (Substitute)
                                </span>
                              )}
                          </div>
                        </div>
                        <div className="text-right">
                          <VisitTime
                            scheduled_at={v.scheduled_at}
                            event_end_time={v.event_end_time}
                          />
                        </div>
                      </div>

                      <div className="flex justify-between items-center mt-2">
                        <StatusBadge status={v.status}>
                          {v.status}
                        </StatusBadge>
                        <span className="text-[11px] text-gray-700">
                          G-sign:{' '}
                          {v.g_sign_confirmed ? (
                            <span className="text-green-600">Yes</span>
                          ) : (
                            <span className="text-red-600">No</span>
                          )}
                        </span>
                      </div>
                      {v.status === 'canceled' && v.notes && (
                        <div className="mt-1 text-[11px] text-purple-700 italic">
                          {v.notes}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notes history modal – isti format kao i ranije */}
      {showNotesHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold">
                Notes History: {notesHistoryStudent}
              </h3>
              <button
                className="text-gray-500"
                onClick={() => setShowNotesHistory(false)}
              >
                ✕
              </button>
            </div>
            {notesHistoryLoading ? (
              <div className="p-4 text-center text-gray-400">
                Loading...
              </div>
            ) : notesHistory.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No notes found for this student.
              </div>
            ) : (
              <div className="space-y-4">
                {notesHistory.map((entry, idx) => (
                  <div
                    key={idx}
                    className="border rounded p-3 bg-gray-50"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex flex-col">
                        <span className="font-semibold text-blue-900">
                          {entry.teacher_name}
                        </span>
                        {entry.discipline_name && (
                          <span className="text-xs text-gray-500">
                            {entry.discipline_name}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {entry.scheduled_at
                          ? entry.scheduled_at
                              .slice(0, 16)
                              .replace('T', ' ')
                          : ''}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {entry.domains && entry.domains.length > 0 ? (
                        entry.domains.map((dom) =>
                          dom.note && dom.note.trim() !== '' ? (
                            <div key={dom.domain_id}>
                              <span className="font-semibold">
                                {dom.name || dom.code || 'Domain'}:
                              </span>{' '}
                              <span>{dom.note}</span>
                            </div>
                          ) : null
                        )
                      ) : (
                        <span className="text-xs text-gray-500 italic">
                          No domain notes for this visit.
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ParentVisitsCalendar;


